var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button, Select, Checkbox, Radio, Form, InputNumber, message, Spin, Typography } from 'antd';
import { RiChatDeleteLine } from 'react-icons/ri';
import { Permissions } from '@whatsper/texterchat-common';
import ModalWithoutBottom from '../../../../Chat/common/ModalWithoutBottom';
import { useHistory } from 'react-router-dom';
import useCheckIfMobile from '../../../../../helpers/hooks/useCheckIfMobile';
import { useStore } from '../../../../../store/rootStore';
import { AUTO_DELETE_PRICING_URL } from '../../../../../constants';
import s from './AutoDeleteSettings.module.scss';
const { Option } = Select;
const { Text } = Typography;
const AutoDeleteSettings = () => {
    const { t } = useTranslation();
    const isMobile = useCheckIfMobile();
    const history = useHistory();
    const [form] = Form.useForm();
    const { settings, agentsStore } = useStore();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    // Long-term deletion (contents)
    const [longTermDuration, setLongTermDuration] = useState('540'); // 18 months
    const [customLongTermValue, setCustomLongTermValue] = useState(540);
    const [customLongTermUnit, setCustomLongTermUnit] = useState('days');
    // Short-term deletion (media or all)
    const [shortTermEnabled, setShortTermEnabled] = useState(true);
    const [shortTermContentType, setShortTermContentType] = useState('media');
    const [shortTermDuration, setShortTermDuration] = useState('7');
    const [customShortTermValue, setCustomShortTermValue] = useState(7);
    const [customShortTermUnit, setCustomShortTermUnit] = useState('days');
    // Keep latest messages
    const [keepLatestEnabled, setKeepLatestEnabled] = useState(false);
    const [keepLatestAfter, setKeepLatestAfter] = useState('90');
    const [customKeepLatestValue, setCustomKeepLatestValue] = useState(90);
    const [customKeepLatestUnit, setCustomKeepLatestUnit] = useState('days');
    const [maxAllowedDays, setMaxAllowedDays] = useState(null);
    const canManageScheduledDeletion = agentsStore.hasPermissions([Permissions.Permission.scheduled_deletion_manage]);
    const canDeleteContents = agentsStore.hasPermissions([Permissions.Permission.messages_delete_contents]);
    const canDeleteMedia = agentsStore.hasPermissions([Permissions.Permission.messages_delete_media]);
    // Validation errors
    const [shortTermError, setShortTermError] = useState(null);
    const [longTermError, setLongTermError] = useState(null);
    useEffect(() => {
        if (!canManageScheduledDeletion) {
            onBack();
            return;
        }
        loadSettings();
    }, []);
    useEffect(() => {
        if (settings.scheduledDeletionSettings && !loading) {
            loadSettings();
        }
    }, [settings.scheduledDeletionSettings]);
    useEffect(() => {
        validateSettings();
    }, [shortTermEnabled, shortTermDuration, customShortTermValue, customShortTermUnit, longTermDuration, customLongTermValue, customLongTermUnit, maxAllowedDays]);
    const convertToMs = (value, customValue, customUnit) => {
        if (value === 'never') {
            return 0;
        }
        if (value === 'custom') {
            const days = customUnit === 'days' ? customValue :
                customUnit === 'weeks' ? customValue * 7 :
                    customUnit === 'months' ? customValue * 30 :
                        customValue * 365;
            return days * 24 * 60 * 60 * 1000;
        }
        return parseInt(value) * 24 * 60 * 60 * 1000;
    };
    const MAX_LONG_TERM_DAYS = 2555; // 7 years
    const MAX_SHORT_TERM_DAYS = 90; // 3 months
    const validateSettings = () => {
        let newShortTermError = null;
        let newLongTermError = null;
        const shortTermMs = shortTermEnabled ? convertToMs(shortTermDuration, customShortTermValue, customShortTermUnit) : 0;
        const longTermMs = convertToMs(longTermDuration, customLongTermValue, customLongTermUnit);
        const maxLongTermMs = MAX_LONG_TERM_DAYS * 24 * 60 * 60 * 1000;
        const maxShortTermMs = MAX_SHORT_TERM_DAYS * 24 * 60 * 60 * 1000;
        if (shortTermMs > 0 && shortTermMs > maxShortTermMs) {
            newShortTermError = 'autoDeleteShortTermExceedsLimit';
        }
        if (longTermMs > 0 && longTermMs > maxLongTermMs) {
            newLongTermError = 'autoDeleteLongTermExceedsLimit';
        }
        if (maxAllowedDays !== null) {
            const maxAllowedMs = maxAllowedDays * 24 * 60 * 60 * 1000;
            if (!newShortTermError && shortTermMs > 0 && shortTermMs > maxAllowedMs) {
                newShortTermError = 'autoDeleteMediaExceedsLimit';
            }
            if (!newLongTermError && longTermMs > 0 && longTermMs > maxAllowedMs) {
                newLongTermError = 'autoDeleteContentsExceedsLimit';
            }
        }
        // Short-term must be shorter than long-term
        if (!newShortTermError && !newLongTermError && shortTermMs > 0 && longTermMs > 0 && shortTermMs >= longTermMs) {
            newShortTermError = 'autoDeleteMediaMustBeLessThanContents';
        }
        setShortTermError(newShortTermError);
        setLongTermError(newLongTermError);
    };
    const loadSettings = () => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        try {
            setLoading(true);
            let data = settings.scheduledDeletionSettings;
            if (!data) {
                data = yield settings.getScheduledDeletionSettings();
            }
            if ((_b = (_a = data.project) === null || _a === void 0 ? void 0 : _a.full) === null || _b === void 0 ? void 0 : _b.olderThanMs) {
                const projectMaxDays = Math.floor(data.project.full.olderThanMs / (1000 * 60 * 60 * 24));
                setMaxAllowedDays(projectMaxDays);
            }
            const { media, contents } = data.custom || {};
            // Load long-term deletion settings
            if (contents && contents.olderThanMs) {
                const days = Math.floor(contents.olderThanMs / (1000 * 60 * 60 * 24));
                const presetValue = ['7', '30', '60', '90', '180', '365', '540'].find(v => parseInt(v) === days);
                if (presetValue) {
                    setLongTermDuration(presetValue);
                }
                else {
                    setLongTermDuration('custom');
                    setCustomLongTermValue(days);
                }
                if (contents.keepForLatestMs) {
                    setKeepLatestEnabled(true);
                    const keepDays = Math.floor(contents.keepForLatestMs / (1000 * 60 * 60 * 24));
                    const keepPresetValue = ['30', '60', '90', '180', '365'].find(v => parseInt(v) === keepDays);
                    if (keepPresetValue) {
                        setKeepLatestAfter(keepPresetValue);
                    }
                    else {
                        setKeepLatestAfter('custom');
                        setCustomKeepLatestValue(keepDays);
                    }
                }
                else {
                    setKeepLatestEnabled(false);
                }
            }
            // Load short-term deletion settings
            if (media && media.olderThanMs) {
                setShortTermEnabled(true);
                const mediaDays = Math.floor(media.olderThanMs / (1000 * 60 * 60 * 24));
                const mediaPresetValue = ['7', '14', '30', '60', '90'].find(v => parseInt(v) === mediaDays);
                if (mediaPresetValue) {
                    setShortTermDuration(mediaPresetValue);
                }
                else {
                    setShortTermDuration('custom');
                    setCustomShortTermValue(mediaDays);
                }
                if (contents && contents.olderThanMs === media.olderThanMs) {
                    setShortTermContentType('all');
                }
                else {
                    setShortTermContentType('media');
                }
            }
        }
        catch (error) {
            console.error('Failed to load auto-delete settings', error);
        }
        finally {
            setLoading(false);
        }
    });
    const onBack = () => {
        history.push('/');
    };
    const onSave = () => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        if (shortTermError || longTermError) {
            message.error(React.createElement(Trans, { i18nKey: "fixValidationErrorsBeforeSaving" }));
            return;
        }
        try {
            setSaving(true);
            const GRACE_PERIOD_MS = 10 * 24 * 60 * 60 * 1000; // 10 days
            const settingsData = {};
            const longTermMs = convertToMs(longTermDuration, customLongTermValue, customLongTermUnit);
            const shortTermMs = shortTermEnabled ? convertToMs(shortTermDuration, customShortTermValue, customShortTermUnit) : 0;
            if (shortTermEnabled && shortTermMs > 0) {
                if (shortTermContentType === 'media') {
                    settingsData.media = {
                        olderThanMs: shortTermMs,
                        gracePeriodMs: GRACE_PERIOD_MS,
                    };
                }
                else if (shortTermContentType === 'all') {
                    settingsData.media = {
                        olderThanMs: shortTermMs,
                        gracePeriodMs: GRACE_PERIOD_MS,
                    };
                    settingsData.contents = {
                        olderThanMs: shortTermMs,
                        gracePeriodMs: GRACE_PERIOD_MS,
                    };
                    if (keepLatestEnabled) {
                        const keepLatestMs = convertToMs(keepLatestAfter, customKeepLatestValue, customKeepLatestUnit);
                        if (keepLatestMs > 0) {
                            settingsData.contents.keepForLatestMs = keepLatestMs;
                        }
                    }
                }
            }
            else {
                if (longTermMs > 0) {
                    settingsData.contents = {
                        olderThanMs: longTermMs,
                        gracePeriodMs: GRACE_PERIOD_MS,
                    };
                    if (keepLatestEnabled) {
                        const keepLatestMs = convertToMs(keepLatestAfter, customKeepLatestValue, customKeepLatestUnit);
                        if (keepLatestMs > 0) {
                            settingsData.contents.keepForLatestMs = keepLatestMs;
                        }
                    }
                }
            }
            yield settings.updateScheduledDeletionSettings(settingsData);
            message.success({
                content: React.createElement(Trans, { i18nKey: "autoDeleteSettingsSaved" }),
                className: s.successMessage,
            });
            onBack();
        }
        catch (error) {
            console.error('Failed to save auto-delete settings', error);
            let errorMessage = 'autoDeleteSettingsSaveError';
            if ((_b = (_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.error) {
                errorMessage = error.response.data.error;
            }
            else if (error === null || error === void 0 ? void 0 : error.message) {
                errorMessage = error.message;
            }
            message.error(errorMessage, 6);
        }
        finally {
            setSaving(false);
        }
    });
    const onCancel = () => {
        onBack();
    };
    const longTermTimeOptions = [
        { value: '90', label: t('timeOption90Days') },
        { value: '180', label: t('timeOption180Days') },
        { value: '365', label: t('timeOption365Days') },
        { value: '540', label: t('timeOption540Days') },
        { value: '730', label: t('timeOption2Years') },
        { value: '1095', label: t('timeOption3Years') },
        { value: '1825', label: t('timeOption5Years') },
        { value: '2555', label: t('timeOption7Years') },
        { value: 'custom', label: t('timeOptionCustom') },
    ];
    const shortTermTimeOptions = [
        { value: '7', label: t('timeOption1Week') },
        { value: '14', label: t('timeOption2Weeks') },
        { value: '30', label: t('timeOption1Month') },
        { value: '60', label: t('timeOption2Months') },
        { value: '90', label: t('timeOption3Months') },
    ];
    const keepLatestTimeOptions = [
        { value: '30', label: t('timeOption30Days') },
        { value: '60', label: t('timeOption60Days') },
        { value: '90', label: t('timeOption90Days') },
        { value: '180', label: t('timeOption180Days') },
        { value: '365', label: t('timeOption365Days') },
        { value: '730', label: t('timeOption2Years') },
        { value: '1095', label: t('timeOption3Years') },
        { value: '1825', label: t('timeOption5Years') },
        { value: '2555', label: t('timeOption7Years') },
        { value: 'custom', label: t('timeOptionCustom') },
    ];
    const unitOptions = [
        { value: 'days', label: t('unitDays') },
        { value: 'weeks', label: t('unitWeeks') },
        { value: 'months', label: t('unitMonths') },
        { value: 'years', label: t('unitYears') },
    ];
    const renderTimeDropdown = (value, onChange, customValue, onCustomValueChange, customUnit, onCustomUnitChange, options, disabled, className) => {
        if (value === 'custom') {
            return (React.createElement("div", { className: s.customInputGroup },
                React.createElement(Select, { value: "custom", onChange: onChange, className: s.customSelect, disabled: disabled }, options.map((option) => (React.createElement(Option, { key: option.value, value: option.value }, option.label)))),
                React.createElement(InputNumber, { value: customValue, onChange: (val) => onCustomValueChange(val || 1), className: s.customNumber, min: 1, max: maxAllowedDays !== null ? maxAllowedDays : undefined, disabled: disabled }),
                React.createElement(Select, { value: customUnit, onChange: onCustomUnitChange, className: s.customUnit, disabled: disabled }, unitOptions.map((option) => (React.createElement(Option, { key: option.value, value: option.value }, option.label))))));
        }
        return (React.createElement(Select, { value: value, onChange: onChange, className: className || s.select, disabled: disabled }, options.map((option) => (React.createElement(Option, { key: option.value, value: option.value }, option.label)))));
    };
    if (loading) {
        return (React.createElement(ModalWithoutBottom, { setModalVisible: onBack, titleTextKey: "autoDeleteSettings", height: "auto", width: isMobile ? undefined : 600, visible: true, headerIcon: () => React.createElement(RiChatDeleteLine, { className: s.titleIcon }) },
            React.createElement("div", { className: s.content },
                React.createElement("div", { className: s.loadingContainer },
                    React.createElement(Spin, { size: "large" })))));
    }
    return (React.createElement(ModalWithoutBottom, { setModalVisible: onBack, titleTextKey: "autoDeleteSettings", height: "auto", width: isMobile ? undefined : 600, visible: true, headerIcon: () => React.createElement(RiChatDeleteLine, { className: s.titleIcon }) },
        React.createElement("div", { className: s.modalBody },
            React.createElement("div", { className: s.content },
                React.createElement("div", { className: s.formContainer },
                    React.createElement(Form, { form: form, layout: "vertical", className: s.form },
                        React.createElement("div", { className: s.section },
                            React.createElement("h3", { className: s.sectionTitle },
                                React.createElement(Trans, { i18nKey: "longTermContentDeletion" })),
                            React.createElement(Form.Item, { label: React.createElement(Trans, { i18nKey: "longTermContentDeletionDescription" }), validateStatus: longTermError ? 'error' : '', help: longTermError && React.createElement(Trans, { i18nKey: longTermError, values: { days: maxAllowedDays } }) }, renderTimeDropdown(longTermDuration, setLongTermDuration, customLongTermValue, setCustomLongTermValue, customLongTermUnit, setCustomLongTermUnit, longTermTimeOptions, !canDeleteContents))),
                        React.createElement("div", { className: s.section },
                            React.createElement("h3", { className: s.sectionTitle },
                                React.createElement(Checkbox, { checked: shortTermEnabled, onChange: (e) => setShortTermEnabled(e.target.checked), disabled: !canDeleteMedia && !canDeleteContents, className: s.checkboxWrapper },
                                    React.createElement(Trans, { i18nKey: "shortTermContentDeletion" }))),
                            shortTermEnabled && (React.createElement(React.Fragment, null,
                                React.createElement(Form.Item, { label: React.createElement(Trans, { i18nKey: "chooseContentType" }) },
                                    React.createElement(Radio.Group, { value: shortTermContentType, onChange: (e) => setShortTermContentType(e.target.value), disabled: !canDeleteMedia && !canDeleteContents, style: { display: 'flex', flexDirection: 'column', gap: 8, margin: '8px 0' } },
                                        React.createElement(Radio, { value: "media", disabled: !canDeleteMedia, className: s.radioOption },
                                            React.createElement(Trans, { i18nKey: "mediaAndFiles" })),
                                        React.createElement(Radio, { value: "all", disabled: !canDeleteMedia || !canDeleteContents, className: s.radioOption },
                                            React.createElement(Trans, { i18nKey: "allMediaFilesAndTextMessages" })))),
                                React.createElement(Form.Item, { label: React.createElement(Trans, { i18nKey: "automaticallyDeleteContentAfter" }), validateStatus: shortTermError ? 'error' : '', help: shortTermError && React.createElement(Trans, { i18nKey: shortTermError, values: { days: maxAllowedDays } }) }, renderTimeDropdown(shortTermDuration, setShortTermDuration, customShortTermValue, setCustomShortTermValue, customShortTermUnit, setCustomShortTermUnit, shortTermTimeOptions, !canDeleteMedia && !canDeleteContents))))),
                        React.createElement("div", { className: s.section },
                            React.createElement(Form.Item, null,
                                React.createElement(Checkbox, { checked: keepLatestEnabled, onChange: (e) => setKeepLatestEnabled(e.target.checked), disabled: !canDeleteContents },
                                    React.createElement(Trans, { i18nKey: "keepLatestMessages" })),
                                keepLatestEnabled && (React.createElement(Text, { type: "secondary", style: { display: 'block', marginTop: '8px', fontSize: '12px' } },
                                    React.createElement(Trans, { i18nKey: "keepLatestMessagesHelp" })))),
                            keepLatestEnabled && (React.createElement(Form.Item, { label: React.createElement(Trans, { i18nKey: "keepLatestMessagesFor" }) }, renderTimeDropdown(keepLatestAfter, setKeepLatestAfter, customKeepLatestValue, setCustomKeepLatestValue, customKeepLatestUnit, setCustomKeepLatestUnit, keepLatestTimeOptions, !canDeleteContents)))),
                        maxAllowedDays !== null && (React.createElement("div", { className: s.infoContainer, style: { marginTop: '24px' } },
                            React.createElement(Text, { type: "warning", style: { display: 'block', fontSize: '13px' } },
                                React.createElement(Trans, { i18nKey: "autoDeleteMaxLimitWarning", values: { days: maxAllowedDays } })))),
                        React.createElement("div", { className: s.pricingNote },
                            React.createElement(Trans, { i18nKey: "autoDeletePricingNote", components: {
                                    1: (React.createElement("a", { href: AUTO_DELETE_PRICING_URL, target: "_blank", rel: "noopener noreferrer", className: s.pricingLink })),
                                } }))))),
            React.createElement("div", { className: s.footer },
                React.createElement(Button, { onClick: onCancel, className: s.actionButton, disabled: saving },
                    React.createElement(Trans, { i18nKey: "cancel" })),
                React.createElement(Button, { type: "primary", onClick: onSave, className: s.actionButton, loading: saving, disabled: saving || (!canDeleteMedia && !canDeleteContents) || !!(shortTermError || longTermError) },
                    React.createElement(Trans, { i18nKey: "save" }))))));
};
export default AutoDeleteSettings;
